package com.example.mystybag.login.logic

import com.example.mystybag.communication.login.LoginCommands.sendEnroll
import com.example.mystybag.communication.utils.onResponse
import com.example.mystybag.config.ApplicationConfig
import com.example.mystybag.core.logic.base.AppContext
import com.example.mystybag.core.logic.base.ChildViewModel
import com.example.mystybag.permissionsUtils.expected.Permission
import com.example.mystybag.permissionsUtils.expected.PermissionHelper
import com.example.mystybag.core.expected.sharedActivity
import com.example.mystybag.notificationsUtils.expected.NotificationHelper
import kotlinx.coroutines.launch
import com.example.mystybag.login.model.WelcomeUIState
import com.example.mystybag.common.logic.NavigatorChooseCity
import com.example.mystybag.common.logic.NavigatorEnterMailAddress
import com.example.mystybag.common.logic.NavigatorInternetTroubleshooting
import com.example.mystybag.common.logic.NavigatorMerchantLogin
import com.example.mystybag.common.logic.NavigatorStaffLogin
import com.example.mystybag.biometricUtils.expected.BiometricsHandler
import com.example.mystybag.biometricUtils.helpers.BiometricAuthenticationType
import com.example.mystybag.communication.Communication
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_CODE
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_CITY
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_CITY_ID
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_DELETE_ACCOUNT_REQUEST_PENDING
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_DIAL_CODE
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_EMAIL
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_FIRST_NAME
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_JWT
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_LAST_NAME
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_BIOMETRIC_PROMPT_DECLINED
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_EMAIL
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_ID
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_JWT
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_PASSWORD
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_USE_BIOMETRIC_FOR_MERCHANT
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_ADD_FIRST_BAG_SKIPPED_MERCHANT_ID
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_IS_STAFF
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_NUMBER_OF_NOT_PICKED_RESERVATIONS
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_NUMBER_OF_RESERVATIONS
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_SAVED_AMOUNT
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_PHONE_NUMBER
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_PHOTO_GUID
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_STAFF_EMAIL
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_STAFF_LOCATION_IDS
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_STAFF_PASSWORD
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_STAFF_BIOMETRIC_PROMPT_DECLINED
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_USE_BIOMETRIC_FOR_STAFF
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_USER_IMG_URL
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_USER_ID
import com.example.mystybag.core.expected.Preferences
import com.example.mystybag.core.helpers.GlobalCommonData.getReferralCodeValue
import com.example.mystybag.core.security.isJwtExpired
import com.example.mystybag.core.helpers.GlobalCommonData
import com.example.mystybag.sharedmodels.mobileAdmin.database.UsersCredentialTypes
import com.example.mystybag.sharedmodels.project.requestModels.EnrollUserRequestModel
import com.example.mystybag.communication.login.LoginCommands
import com.example.mystybag.core.expected.LogHelper
import com.example.mystybag.core.expected.signInWithApple
import com.example.mystybag.core.expected.signInWithFacebook
import com.example.mystybag.core.expected.signInWithGoogle
import com.example.mystybag.sharedmodels.project.requestModels.CheckUserExistsRequestModel

class WelcomeViewModel(
    context: AppContext,
    private val fromBackButton: Boolean = false
) : ChildViewModel<WelcomeUIState>(context, WelcomeUIState()) {

    override fun onCreate() {
        navControls.backButtonVisible   = false
        navControls.leadingIcon         = null  // No logo/icon on Welcome screen (per Figma)

        // Web (mobile): we just returned from the full-page Google OAuth redirect. Re-trigger the
        // Google flow so signInWithGoogle() consumes the access token from the URL fragment and
        // continues the normal check-user-exists/enroll path. Prefs were already reset before the
        // redirect, so getInitialNavigatorConfig() lands here on Welcome.
        if (com.example.mystybag.core.expected.hasPendingGoogleRedirect()) {
            onGoogleLogin()
            return
        }

        // User pressed Back from login screen: show Welcome without auto-navigating to merchant/staff login.
        if (fromBackButton) return

        val mail = Preferences.getString(key = PREFERENCES_EMAIL, "")
        val code = Preferences.getString(key = PREFERENCES_CODE, "")
        val merchantJwt = Preferences.getString(key = PREFERENCES_MERCHANT_JWT, "")
        val merchantEmail = Preferences.getString(key = PREFERENCES_MERCHANT_EMAIL, "")
        val useBiometricForMerchant = Preferences.getBoolean(key = PREFERENCES_USE_BIOMETRIC_FOR_MERCHANT, false)

        val isStaff = Preferences.getBoolean(key = PREFERENCES_IS_STAFF, false)
        val staffEmail = Preferences.getString(key = PREFERENCES_STAFF_EMAIL, "")
        val useBiometricForStaff = Preferences.getBoolean(key = PREFERENCES_USE_BIOMETRIC_FOR_STAFF, false)

        // Check for merchant/staff login first (before user login)
        if (merchantJwt.isNotEmpty()) {
            if (isJwtExpired(merchantJwt)) {
                // Token expired: clear JWT and take user to merchant or staff login (email pre-filled); biometric triggered there when enabled.
                Preferences.setString(PREFERENCES_MERCHANT_JWT, "")
                launchOnMain {
                    if (isStaff && staffEmail.isNotEmpty()) {
                        navigate(NavigatorStaffLogin)
                    } else if (merchantEmail.isNotEmpty()) {
                        navigate(NavigatorMerchantLogin)
                    }
                }
            } else {
                // JWT valid: restore session only if biometric was enabled for the current type (merchant or staff).
                if (isStaff) {
                    if (!useBiometricForStaff) {
                        Preferences.setString(PREFERENCES_MERCHANT_JWT, "")
                        if (staffEmail.isNotEmpty()) {
                            launchOnMain { navigate(NavigatorStaffLogin) }
                        }
                        return
                    }
                    val biometricAvailable =
                        BiometricsHandler.getAuthenticationType() != BiometricAuthenticationType.NONE &&
                            BiometricsHandler.isBiometricScannerAvailableAndSet()
                    if (biometricAvailable) {
                        launchOnMain { navigate(NavigatorStaffLogin) }
                    } else {
                        Communication.token = merchantJwt
                        launchOnMain {
                            setResult<LoginInterface> {
                                goToMainScreen(false, onErrorCallback = {
                                    updateState { uiState -> uiState.copy() }
                                })
                            }
                        }
                    }
                } else {
                    if (!useBiometricForMerchant) {
                        Preferences.setString(PREFERENCES_MERCHANT_JWT, "")
                        if (merchantEmail.isNotEmpty()) {
                            launchOnMain { navigate(NavigatorMerchantLogin) }
                        }
                        return
                    }
                    val biometricAvailable =
                        BiometricsHandler.getAuthenticationType() != BiometricAuthenticationType.NONE &&
                            BiometricsHandler.isBiometricScannerAvailableAndSet()
                    if (biometricAvailable) {
                        launchOnMain { navigate(NavigatorMerchantLogin) }
                    } else {
                        Communication.token = merchantJwt
                        launchOnMain {
                            setResult<LoginInterface> {
                                goToMainScreen(false, onErrorCallback = {
                                    updateState { uiState -> uiState.copy() }
                                })
                            }
                        }
                    }
                }
            }
        } else if (isStaff && staffEmail.isNotEmpty()) {
            // No JWT but we have saved staff email (e.g. after expiry) -> open staff login with email pre-filled; biometric triggered there when enabled.
            launchOnMain { navigate(NavigatorStaffLogin) }
        } else if (merchantEmail.isNotEmpty()) {
            // No JWT but we have saved merchant email -> take user to merchant login with email pre-filled (or biometric)
            launchOnMain { navigate(NavigatorMerchantLogin) }
        } else if(!ApplicationConfig.isMerchantOnlyBuild && mail.isNotEmpty() && code.isNotEmpty()) {
            // User is logged in - navigate directly to HomePage where login will be handled.
            // Skipped for merchant-only builds: no customer flow is exposed there.
            launchOnMain {
                setResult<LoginInterface> {
                    goToMainScreen(false, onErrorCallback = {
                        updateState { uiState -> uiState.copy() }
                    })
                }
            }
        } else {
            scope.launch {
                if (PermissionHelper.requestPermission(Permission.Notification, sharedActivity))
                    NotificationHelper.setNotificationChannels()
            }
        }
    }

    private fun resetStoredLoginPreferences() {
        Communication.token = ""
        GlobalCommonData.resetData()

        listOf(
            PREFERENCES_CODE,
            PREFERENCES_EMAIL,
            PREFERENCES_USER_ID,
            PREFERENCES_JWT,
            PREFERENCES_PHONE_NUMBER,
            PREFERENCES_DIAL_CODE,
            PREFERENCES_FIRST_NAME,
            PREFERENCES_LAST_NAME,
            PREFERENCES_CITY,
            PREFERENCES_CITY_ID,
            PREFERENCES_PHOTO_GUID,
            PREFERENCES_USER_IMG_URL,
            PREFERENCES_NUMBER_OF_RESERVATIONS,
            PREFERENCES_NUMBER_OF_NOT_PICKED_RESERVATIONS,
            PREFERENCES_SAVED_AMOUNT,
            PREFERENCES_DELETE_ACCOUNT_REQUEST_PENDING,
            PREFERENCES_MERCHANT_JWT,
            PREFERENCES_MERCHANT_ID,
            PREFERENCES_MERCHANT_EMAIL,
            PREFERENCES_MERCHANT_PASSWORD,
            PREFERENCES_USE_BIOMETRIC_FOR_MERCHANT,
            PREFERENCES_MERCHANT_BIOMETRIC_PROMPT_DECLINED,
            PREFERENCES_ADD_FIRST_BAG_SKIPPED_MERCHANT_ID,
            PREFERENCES_IS_STAFF,
            PREFERENCES_STAFF_EMAIL,
            PREFERENCES_STAFF_LOCATION_IDS,
            PREFERENCES_STAFF_PASSWORD,
            PREFERENCES_USE_BIOMETRIC_FOR_STAFF,
            PREFERENCES_STAFF_BIOMETRIC_PROMPT_DECLINED
        ).forEach(Preferences::remove)
    }

    fun onGoogleLogin() {
        resetStoredLoginPreferences()
        LogHelper.logEvent("[GLOG] onGoogleLogin: start")
        scope.launch {
            try {
                val googleResult = signInWithGoogle()
                LogHelper.logEvent(
                    "[GLOG] signInWithGoogle -> " + if (googleResult == null) "null" else
                        "email='${googleResult.email}' first='${googleResult.firstName ?: "-"}' " +
                            "last='${googleResult.lastName ?: "-"}' photoLen=${googleResult.photoUrl?.length ?: 0}"
                )

                if (googleResult != null) {
                    if (googleResult.email.isNotEmpty()) {
                        sendCheckUserExists(
                            email = googleResult.email,
                            credentialTypes = UsersCredentialTypes.GOOGLE,
                            firstName = googleResult.firstName,
                            lastName = googleResult.lastName,
                            photoUrl = googleResult.photoUrl,
                        )
                    } else {
                        showSnackbar("Failed to get email from Google Sign-In")
                    }
                } else {
                    showSnackbar("Google Sign-In was cancelled or failed")
                }
            } catch (e: Exception) {
                LogHelper.logEvent("[GLOG] onGoogleLogin exception: ${e.message}")
                showSnackbar("Error during Google Sign-In: ${e.message}")
            }
        }
    }

    private fun sendCheckUserExists(
        email: String,
        credentialTypes: UsersCredentialTypes,
        firstName: String? = null,
        lastName: String? = null,
        photoUrl: String? = null,
    ) {
        updateState { it.copy(isLoading = true) }
        val request = CheckUserExistsRequestModel(
            email = email,
            firstName = firstName,
            lastName = lastName,
            photoUrl = photoUrl,
        )
        LogHelper.logEvent("[GLOG] sendCheckUserExists -> email='$email' type=$credentialTypes first='${firstName ?: "-"}' last='${lastName ?: "-"}' photoLen=${photoUrl?.length ?: 0}")

        commandBlock {
            LoginCommands.sendCheckUserExists(request)
                .onResponse(
                    error = { e ->
                        LogHelper.logEvent("[GLOG] checkUserExists TYPED-error status=${e.status} ep='${e.apiEndPoint}' msg='${e.statusMessage ?: "<null>"}'")
                        launchOnMain { updateState { it.copy(isLoading = false) } }
                        showSnackbar("ERR check status=${e.status} ep=${e.apiEndPoint} msg=${e.statusMessage ?: "-"}")
                    },
                    success = { response ->
                        LogHelper.logEvent("[GLOG] checkUserExists SUCCESS isExistingUser=${response.isExistingUser}")
                        launchOnMain {
                            updateState { it.copy(isLoading = false) }
                            if (response.isExistingUser) {
                                enrollCommand(
                                    email = email,
                                    credentialTypes = credentialTypes,
                                    firstName = firstName,
                                    lastName = lastName,
                                    photoUrl = photoUrl,
                                )
                            } else {
                                navigate(NavigatorChooseCity(
                                    userId = 0L,
                                    email = email,
                                    credentialTypes = credentialTypes,
                                    firstName = firstName,
                                    lastName = lastName,
                                    photoUrl = photoUrl,
                                ))
                            }
                        }
                    },
                    baseError = { response ->
                        LogHelper.logEvent("[GLOG] checkUserExists BASE-error status=${response.status} ep='${response.apiEndPoint}' msg='${response.statusMessage ?: "<null>"}'")
                        handleError(response)
                    }
                )
        }
    }

    private fun enrollCommand(
        email: String,
        credentialTypes: UsersCredentialTypes,
        firstName: String? = null,
        lastName: String? = null,
        photoUrl: String? = null,
    ) {
        updateState { it.copy(isLoading = true) }
        val request = EnrollUserRequestModel(
            email = email,
            credentialTypes = credentialTypes,
            firstName = firstName,
            lastName = lastName,
            photoUrl = photoUrl,
            referralCode = getReferralCodeValue(),
        )
        LogHelper.logEvent("[GLOG] enroll -> email='$email' type=$credentialTypes")

        commandBlock {
            sendEnroll(request = request)
                .onResponse(
                    error = { e ->
                        LogHelper.logEvent("[GLOG] enroll TYPED-error status=${e.status} ep='${e.apiEndPoint}' msg='${e.statusMessage ?: "<null>"}'")
                        launchOnMain { updateState { it.copy(isLoading = false) } }
                        showSnackbar("ERR enroll status=${e.status} ep=${e.apiEndPoint} msg=${e.statusMessage ?: "-"}")
                    },
                    success = { response ->
                        LogHelper.logEvent("[GLOG] enroll SUCCESS userId=${response.userId} codeLen=${response.code.length}")
                        // Save email
                        Preferences.setString(PREFERENCES_EMAIL, email)
                        Preferences.setString(PREFERENCES_CODE, response.code)
                        Preferences.setLong(PREFERENCES_USER_ID, response.userId)

                        launchOnMain {
                            updateState { it.copy(isLoading = false) }

                            setResult<LoginInterface> {
                                goToMainScreen(false, onErrorCallback = {
                                    updateState { uiState -> uiState.copy() }
                                })
                            }
                        }
                    },
                    baseError = { response ->
                        LogHelper.logEvent("[GLOG] enroll BASE-error status=${response.status} ep='${response.apiEndPoint}' msg='${response.statusMessage ?: "<null>"}'")
                        handleError(response)
                    }
                )
        }
    }
    
    private suspend fun handleError(model: com.example.mystybag.sharedmodels.base.BaseResponseModel) {
        updateState { it.copy(isLoading = false) }
        when (model.status) {
            com.example.mystybag.sharedmodels.defines.ServerStatusDefines.STATUS_MANDATORY_UPDATE -> 
                showDialog(com.example.mystybag.common.logic.DialogNavigatorForceUpdate)
                    .onPositiveClick { com.example.mystybag.core.expected.openAppStorePage() }
            // TEMP [GLOG]: surface the real status/endpoint/message on-screen so we can read it on
            // mobile without devtools. Revert to getErrorMessage(...) once the social-login issue is fixed.
            else -> {
                LogHelper.logEvent("[GLOG] handleError status=${model.status} ep='${model.apiEndPoint}' msg='${model.statusMessage ?: "<null>"}'")
                showSnackbar("ERR status=${model.status} ep=${model.apiEndPoint} msg=${model.statusMessage ?: "-"}")
            }
        }
    }

    fun onAppleLogin() {
        resetStoredLoginPreferences()
        scope.launch {
            try {
                val appleResult = signInWithApple()

                if (appleResult != null) {
                    // Apple only fills the dedicated email field on the FIRST authorization. On
                    // subsequent sign-ins it's empty, but Apple still ships the `email` claim inside
                    // the identity-token JWT, so fall back to decoding it from there.
                    val email = appleResult.email.ifEmpty {
                        com.example.mystybag.core.security.extractJwtClaimString(appleResult.idToken, "email").orEmpty()
                    }
                    if (email.isNotEmpty()) {
                        sendCheckUserExists(
                            email = email,
                            credentialTypes = UsersCredentialTypes.APPLE,
                            firstName = appleResult.firstName,
                            lastName = appleResult.lastName,
                            photoUrl = null,
                        )
                    } else {
                        // No email in the credential nor in the identity token — should not normally
                        // happen. Backend can still resolve the user from the JWT `sub` if needed.
                        LogHelper.logEvent("[AppleSignIn] no email in credential or idToken")
                        showSnackbar("Failed to get email from Apple Sign-In")
                    }
                } else {
                    showSnackbar("Apple Sign-In was cancelled or failed")
                }
            } catch (e: Exception) {
                showSnackbar("Error during Apple Sign-In: ${e.message}")
            }
        }
    }

    fun onFacebookLogin() {
        resetStoredLoginPreferences()
        scope.launch {
            try {
                val fbResult = signInWithFacebook()

                if (fbResult != null) {
                    if (fbResult.email.isNotEmpty()) {
                        sendCheckUserExists(
                            email = fbResult.email,
                            credentialTypes = UsersCredentialTypes.FACEBOOK,
                            firstName = fbResult.firstName,
                            lastName = fbResult.lastName,
                            photoUrl = fbResult.photoUrl,
                        )
                    } else {
                        // Facebook may omit email if the user denied the `email` scope or
                        // their FB account has no verified email. We cannot enroll the user
                        // without an email on the client side.
                        LogHelper.logEvent(
                            "[FacebookSignIn] empty email returned (user likely denied email scope " +
                                "or has no verified FB email). userId=${fbResult.userId}"
                        )
                        showSnackbar("Failed to get email from Facebook Sign-In")
                    }
                } else {
                    showSnackbar("Facebook Sign-In was cancelled or failed")
                }
            } catch (e: Exception) {
                showSnackbar("Error during Facebook Sign-In: ${e.message}")
            }
        }
    }

    fun onEmailLogin() {
        resetStoredLoginPreferences()
        navigate(NavigatorEnterMailAddress)
    }

    fun onMerchantLogin() {
        resetStoredLoginPreferences()
        navigate(NavigatorMerchantLogin)
    }

    fun onStaffLogin() {
        resetStoredLoginPreferences()
        navigate(NavigatorStaffLogin)
    }
    
    fun onMerchantRegistration() {
        resetStoredLoginPreferences()
        navigate(
            NavigatorMerchantRegistrationStep1(
                sharedConfig = MerchantRegistrationSharedConfig(
                    referralCode = getReferralCodeValue()
                )
            )
        ) // Step 1: Add company details
    }

    /** Called when user selects Client or Merchant in the segment control. */
    fun onTabSelected(isClient: Boolean) {
        updateState { it.copy(isClientTabSelected = isClient) }
    }

    fun onHelpClick() {
        navigate(NavigatorInternetTroubleshooting)
    }

}